import autobind from 'autobind-decorator';
import PropTypes from 'prop-types';
import { Component } from 'react';
import Notifications from 'react-notification-system-redux';
import { connect } from 'react-redux';

import { bindActionCreators } from 'redux';
import { InputProps } from '../../../../common/components/form-elements/Input';
import * as Actions from '../../../../common/services/customer/Actions';
import * as CustomerService from '../../../../common/services/customer/Service';
import { CustomerActionPropTypes, CustomerError } from '../../../../common/services/customer/Types';
import { ChangePasswordDTO } from '../../../../common/types/dto/ChangePasswordDTO';
import { CustomerDataDTO } from '../../../../common/types/dto/CustomerDataDTO';
import { ConfigProp, RenderPropWithProps, TranslationFuncProp } from '../../../../common/types/prop-types/common';
import { RootState } from '../../../base/RootReducer';
import { config } from '../../../common/';
import { AppThunkDispatch } from '../../../common/types';

export interface DataAccessProps extends ConfigProp {
    editState: EditState;
    email: InputProps;
    password: InputProps;
    passwordConfirm: InputProps;
    oldPassword: InputProps;
    submitErrorText: string;

    btnEditDataAccess: {
        onClick: (event: React.MouseEvent<{}> | null) => void;
        value: string;
        disabled: boolean;
    };
}

export interface DataAccessContainerProps {
}

export interface EditState {
    isEditing: boolean;
    changeEditMode: (event: React.MouseEvent<{}> | null) => void;
}

interface ReduxStateProps {
    isCommunicating: boolean;
    customerInfo: CustomerDataDTO | undefined;
}

interface ReduxDispatchProps {
    customerActions: typeof Actions;
    customerServiceActions: CustomerService.ServiceActions;
    dispatch: AppThunkDispatch;
}

interface DataAccessContainerState {
    isEditing: boolean;
    email: string;
    emailValMsg: string;
    password: string;
    passwordValMsg: string;
    passwordConfirm: string;
    passwordConfirmValMsg: string;
    oldPassword: string;
    oldPasswordValMsg: string;
    submitErrorText: string;
}

class DataAccessContainer extends Component<
    DataAccessContainerProps &
    TranslationFuncProp &
    RenderPropWithProps<DataAccessProps> &
    ReduxStateProps &
    ReduxDispatchProps,
    DataAccessContainerState
    > {
    public static propTypes = {
        t: PropTypes.func.isRequired,
        isCommunicating: PropTypes.bool.isRequired,
        customerActions: CustomerActionPropTypes.isRequired,
        customerServiceActions: CustomerService.ServiceActionPropTypes.isRequired,
        customerInfo: PropTypes.any,
        dispatch: PropTypes.func.isRequired,
        //
        children: PropTypes.func.isRequired,
    };

    public readonly state: DataAccessContainerState = {
        isEditing: false,
        email: '',
        emailValMsg: '',
        password: '',
        passwordValMsg: '',
        passwordConfirm: '',
        passwordConfirmValMsg: '',
        oldPassword: '',
        oldPasswordValMsg: '',
        submitErrorText: '',
    };

    public componentWillReceiveProps(nextProps: ReduxStateProps) {
        this.handleInit(nextProps);
    }

    public componentDidMount() {
        this.handleInit(this.props);
    }

    public handleInit(nextProps: ReduxStateProps) {
        if (nextProps.customerInfo) {
            this.setState({
                email: nextProps.customerInfo.email || this.state.email,
            });
        }
    }

    @autobind
    public onFieldKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            this.onEditSubmit(null);
        }
    }

    public validateForm() {
        if (this.props.isCommunicating) {
            return false;
        }
        let isValid: boolean = true;
        if (this.state.email === '') {
            this.setState({ emailValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (!config.Constants.Regex.EmailValidation.test(this.state.email)) {
            this.setState({ emailValMsg: this.props.t('signup:errors:emailValidationRegex') });
            isValid = false;
        }
        if (this.state.password === '') {
            this.setState({ passwordValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (!config.Constants.Regex.PasswordValidation.test(this.state.password)) {
            this.setState({ passwordValMsg: this.props.t('signup:errors:passwordValidationRegex') });
            isValid = false;
        }
        if (this.state.passwordConfirm === '' || this.state.passwordConfirm !== this.state.password) {
            this.setState({ passwordConfirmValMsg: this.props.t('signup:errors:confirmPasswordFail') });
            isValid = false;
        }
        if (this.state.oldPassword === '') {
            this.setState({ oldPasswordValMsg: this.props.t('mandatory') });
            isValid = false;
        }
        return isValid;
    }

    public changeEditMode = (e: React.MouseEvent<{}> | null) => {
        if (e) {
            e.preventDefault();
        }
        this.setState({
            isEditing: !this.state.isEditing,
        });
    }

    @autobind
    public async onEditSubmit(e: React.MouseEvent<{}> | null) {
        if (e) {
            e.preventDefault();
        }
        if (!this.validateForm()) {
            return;
        }
        try {
            const changePasswordDTO: ChangePasswordDTO = {
                newPassword: this.state.password,
                oldPassword: this.state.oldPassword,
            };
            await this.props.customerServiceActions.changePassword(changePasswordDTO);
            this.props.dispatch(Notifications.success({
                title: this.props.t('success'),
                message: this.props.t('profile:changePassword:success'),
                position: 'tc',
                autoDismiss: 5,
            }));
            this.setState({
                password: '',
                oldPassword: '',
                passwordConfirm: '',
                submitErrorText: '',
                isEditing: false,
            });
        } catch (error) {
            if (error instanceof CustomerError) {
                this.setState({ submitErrorText: this.props.t('signup:changePasswordError') });
                this.props.dispatch(Notifications.error({
                    title: this.props.t('error'),
                    message: this.props.t('signup:changePasswordError'),
                    position: 'tc',
                    autoDismiss: 5,
                }));
            } else {
                throw error;
            }
        }
    }

    @autobind
    public onEmailChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ email: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ emailValMsg: '' });
        } else {
            this.setState({ emailValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onPasswordChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ password: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ passwordValMsg: '' });
        } else {
            this.setState({ passwordValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onPasswordConfirmChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ passwordConfirm: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ passwordConfirmValMsg: '' });
        } else {
            this.setState({ passwordConfirmValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onOldPasswordChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ oldPassword: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ oldPasswordValMsg: '' });
        } else {
            this.setState({ oldPasswordValMsg: this.props.t('mandatory') });
        }
    }

    public render() {
        const newProps: DataAccessProps = {
            editState: {
                isEditing: this.state.isEditing,
                changeEditMode: this.changeEditMode,
            },
            email: {
                inputProps: {
                    value: this.state.email,
                    onChange: this.onEmailChange,
                },
                errorText: this.state.emailValMsg,
                hintText: '',
            },
            password: {
                inputProps: {
                    value: this.state.password,
                    onChange: this.onPasswordChange,
                    type: 'password',
                },
                errorText: this.state.passwordValMsg,
                hintText: '',
            },
            passwordConfirm: {
                inputProps: {
                    value: this.state.passwordConfirm,
                    onChange: this.onPasswordConfirmChange,
                    type: 'password',
                },
                errorText: this.state.passwordConfirmValMsg,
                hintText: '',
            },
            oldPassword: {
                inputProps: {
                    value: this.state.oldPassword,
                    onChange: this.onOldPasswordChange,
                    type: 'password',
                },
                errorText: this.state.oldPasswordValMsg,
                hintText: '',
            },
            submitErrorText: this.state.submitErrorText,
            btnEditDataAccess: {
                onClick: this.onEditSubmit,
                value: this.props.t('signup:signup'),
                disabled: this.props.isCommunicating,
            },
            config,
        };

        return this.props.children(newProps);
    }
}

const mapStateToProps = (store: RootState) => ({
    isCommunicating: store.customerState.changePassword.isCommunicating,
    customerInfo: store.customerState.customerData.data.customerData,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    customerActions: bindActionCreators(Actions, dispatch),
    customerServiceActions: CustomerService.bindServiceActionCreators(config, dispatch),
    dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(DataAccessContainer);
